/**
 * AgentBadge — the agent's colour square + white glyph. The single shared
 * render unit of the agent-identity system (agentIdentity.ts), used everywhere
 * an agent appears: the conversation card, agent-group headers, launcher tiles,
 * the agent strip, and the DEV preview.
 *
 * `size` maps to the square + radius + glyph dimensions; colour comes from the
 * resolved identity's accent (`identity.accent.badge`).
 */
import { cn } from '@/lib/utils'
import type { AgentIdentity } from '@/lib/agentIdentity'

export type AgentBadgeSize = 'sm' | 'md' | 'lg' | 'xl'

const SIZES: Record<AgentBadgeSize, { box: string; glyph: string }> = {
  sm: { box: 'h-[18px] w-[18px] rounded-[5px]', glyph: 'h-[11px] w-[11px]' },
  md: { box: 'h-5 w-5 rounded-[5px]', glyph: 'h-3 w-3' },
  lg: { box: 'h-7 w-7 rounded-md', glyph: 'h-4 w-4' },
  xl: { box: 'h-[34px] w-[34px] rounded-[10px]', glyph: 'h-[18px] w-[18px]' },
}

export function AgentBadge({
  identity,
  size = 'sm',
  className,
}: {
  identity: AgentIdentity
  size?: AgentBadgeSize
  className?: string
}) {
  const { Icon } = identity
  const s = SIZES[size]
  return (
    <span
      className={cn('flex flex-none items-center justify-center', s.box, identity.accent.badge, className)}
      aria-hidden="true"
    >
      <Icon className={s.glyph} />
    </span>
  )
}
